import {
  ChannelDetails,
  ChannelTypeEnum,
  dispatch,
  getAllChannelsAndThreads,
  getCurrentChannelId,
  getWorkspaceId,
  store,
  updateCurrentChannel,
  updateUnreadMessageCount,
  upsertChannel,
  useAppSelector,
  useLazyGetChannelDetailsQuery,
  useLazySearchCustomerQuery,
} from '@zonofi/seller-rtk-store';
import { useCallback, useEffect, useState } from 'react';
import { useUpdateLastVisitedTime } from './update-last-visited-time';

interface GetChannelDetailsProps {
  onSuccess?: (data?: ChannelDetails) => void;
  onError?: () => void;
}

export const useGetChannelDetails = ({
  onSuccess,
  onError,
}: GetChannelDetailsProps) => {
  const workspaceId = getWorkspaceId(store.getState());
  const allChannels = getAllChannelsAndThreads(store.getState());

  const { updateLastVisit } = useUpdateLastVisitedTime();

  const [triggerGetChannelDetails, channelDetailsResponse] =
    useLazyGetChannelDetailsQuery();
  const [triggerCustomerSearch, customerResponse] =
    useLazySearchCustomerQuery();

  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [activeChannelId, setActiveChannelId] = useState('');

  /** ---------------------------
   * Handle errors
   ---------------------------- */
  const handleError = useCallback(() => {
    onError?.();
    setIsLocalLoading(false);
  }, [onError]);

  /** ---------------------------
   * Get Channel Details by channelId
   ---------------------------- */
  const getChannelDetails = useCallback(
    async (channelId: string, isSubChannel?: boolean) => {
      setActiveChannelId(channelId);
      setIsLocalLoading(true);

      try {
        const sellerWorkspaceId =
          allChannels?.[channelId]?.sellerWorkspaceId || workspaceId;

        if (!isSubChannel) {
          dispatch(updateCurrentChannel(channelId));
        }

        dispatch(updateUnreadMessageCount({ [channelId]: 0 }));

        const data = await triggerGetChannelDetails({
          channelId,
          workspaceId,
          sellerWorkspaceId,
        }).unwrap();

        const previousChannelId = getCurrentChannelId(store.getState());
        const isCustomerThread =
          data.channelType === 'C' ||
          data.type === ChannelTypeEnum.CUSTOMERTHREAD;

        // Update last visit times
        if (previousChannelId && previousChannelId !== channelId) {
          updateLastVisit({ channelId: previousChannelId });
        }
        updateLastVisit({ channelId });

        // Fetch customer details if available
        if (data?.customerId) {
          triggerCustomerSearch({
            workspaceId,
            inviteId: data.customerId,
            includeInactive: true,
            includeCFA: false,
          });
        }

        onSuccess?.(data);
      } catch {
        handleError();
      } finally {
        setIsLocalLoading(false);
      }
    },
    [
      allChannels,
      workspaceId,
      triggerGetChannelDetails,
      triggerCustomerSearch,
      handleError,
      onSuccess,
      updateLastVisit,
    ]
  );

  /** ---------------------------
   * Get Channel Details by customerId + channelType
   ---------------------------- */
  const getChannelDetailsByCustomer = useCallback(
    async (customerId: string, channelType: string) => {
      setIsLocalLoading(true);

      try {
        const data = await triggerGetChannelDetails({
          customerId,
          workspaceId,
          sellerWorkspaceId: workspaceId,
          channelType,
        }).unwrap();

        dispatch(updateCurrentChannel(data?.id));
        onSuccess?.(data);
      } catch {
        handleError();
      } finally {
        setIsLocalLoading(false);
      }
    },
    [workspaceId, triggerGetChannelDetails, onSuccess, handleError]
  );

  /** ---------------------------
   * Handle customer search results
   ---------------------------- */
  useEffect(() => {
    if (!activeChannelId) return;

    const { isLoading, isSuccess, data: customer } = customerResponse;

    // Always update loading state for customer info
    dispatch(
      upsertChannel({
        [activeChannelId]: { customerIsLoading: isLoading },
      })
    );

    if (isSuccess && customer) {
      dispatch(
        upsertChannel({
          [activeChannelId]: {
            isLockedCustomer: customer?.isActive === 0,
            customerInfo: customer,
          },
        })
      );
    }
  }, [activeChannelId, customerResponse]);

  return {
    getChannelDetails,
    getChannelDetailsByCustomer,
    channelDetails: channelDetailsResponse.data,
    channelDetailsIsLoading:
      channelDetailsResponse.isLoading ||
      channelDetailsResponse.isFetching ||
      isLocalLoading,
    channelDetailsArgs: channelDetailsResponse.originalArgs,
  };
};
